import React, { createContext, useContext, useState, useCallback, useMemo, useRef, useEffect } from 'react';

/**
 * Emergency Alert Context
 * 시스템 전체에서 Emergency 알람 상태를 관리합니다.
 */

const EmergencyContext = createContext();

export const useEmergency = () => {
  const context = useContext(EmergencyContext);
  if (!context) {
    throw new Error('useEmergency must be used within an EmergencyProvider');
  }
  return context;
};

export const EmergencyProvider = ({ children }) => {
  const [emergencyState, setEmergencyState] = useState({
    isActive: false,
    level: null, // 'emergency'
    triggeredBy: [], // Emergency를 트리거한 차트들
    timestamp: null,
    isAcknowledged: false, // 사용자가 확인했는지 (오버레이 숨김용)
    lastTrigger: null
  });

  // 🔊 Emergency 사운드 관리 (안전한 구현)
  const audioContextRef = useRef(null);
  const oscillatorRef = useRef(null);
  const gainNodeRef = useRef(null);
  const soundIntervalRef = useRef(null);
  const userInteractedRef = useRef(false);

  // 🚨 사이렌 소리 시작 함수 (안전한 버전)
  const startEmergencySound = useCallback(() => {
    try {
      // 1. 기존 사운드 완전 정리 (비동기로 처리하지 않고 즉시)
      if (soundIntervalRef.current) {
        clearInterval(soundIntervalRef.current);
        soundIntervalRef.current = null;
      }
      
      if (gainNodeRef.current) {
        try { gainNodeRef.current.disconnect(); } catch (e) {}
        gainNodeRef.current = null;
      }
      
      if (audioContextRef.current && audioContextRef.current.state !== 'closed') {
        try { audioContextRef.current.close(); } catch (e) {}
      }
      audioContextRef.current = null;

      // 2. AudioContext 생성 (브라우저 호환성)
      const AudioContext = window.AudioContext || window.webkitAudioContext;
      if (!AudioContext) {
        return;
      }

      // 3. 새로운 AudioContext 생성
      audioContextRef.current = new AudioContext();
      const audioContext = audioContextRef.current;

      // 브라우저 자동재생 정책 대응
      if (audioContext.state === 'suspended') {
        // 사용자 상호작용이 있었다면 즉시 resume 시도
        if (userInteractedRef.current) {
          audioContext.resume().then(() => {
          }).catch(error => {
          });
        } else {
          // 사용자에게 클릭을 유도하는 메시지 표시 가능
        }
      }

      // 4. 게인 노드 생성 (볼륨 조절)
      gainNodeRef.current = audioContext.createGain();
      gainNodeRef.current.connect(audioContext.destination);
      gainNodeRef.current.gain.value = 0.8; // 시끄러운 볼륨

      // 사이렌 소리 패턴 생성 (안전한 버전)
      const createSirenTone = (frequency, duration) => {
        // AudioContext와 gainNode 유효성 검증
        if (!audioContextRef.current || audioContextRef.current.state === 'closed') {
          return null;
        }

        if (!gainNodeRef.current) {
          return null;
        }

        try {
          const currentAudioContext = audioContextRef.current;
          const currentGainNode = gainNodeRef.current;
          
          // 같은 AudioContext에서 oscillator 생성
          const oscillator = currentAudioContext.createOscillator();
          oscillator.type = 'sawtooth'; // 사이렌 같은 톤
          oscillator.frequency.setValueAtTime(frequency, currentAudioContext.currentTime);
          
          // 주파수 변조 (사이렌 효과)
          oscillator.frequency.exponentialRampToValueAtTime(
            frequency * 1.5, 
            currentAudioContext.currentTime + duration / 2
          );
          oscillator.frequency.exponentialRampToValueAtTime(
            frequency, 
            currentAudioContext.currentTime + duration
          );

          // 같은 AudioContext의 nodes끼리만 연결
          oscillator.connect(currentGainNode);
          oscillator.start(currentAudioContext.currentTime);
          oscillator.stop(currentAudioContext.currentTime + duration);
          
          return oscillator;
        } catch (error) {
          return null;
        }
      };

      // 반복적인 사이렌 패턴 (안전한 버전)
      const playSirenPattern = () => {
        // AudioContext 상태 확인
        if (!audioContextRef.current || audioContextRef.current.state === 'closed') {
          return;
        }

        if (!gainNodeRef.current) {
          return;
        }

        // 3단계 사이렌: 높은 톤 → 중간 톤 → 낮은 톤
        createSirenTone(1000, 0.3); // 매우 높은 톤
        
        setTimeout(() => {
          if (audioContextRef.current && audioContextRef.current.state !== 'closed' && gainNodeRef.current) {
            createSirenTone(700, 0.3); // 중간 톤
          }
        }, 250);
        
        setTimeout(() => {
          if (audioContextRef.current && audioContextRef.current.state !== 'closed' && gainNodeRef.current) {
            createSirenTone(400, 0.3); // 낮은 톤
          }
        }, 500);
      };

      // 5. 초기 사이렌 시작
      playSirenPattern();

      // 6. 0.8초마다 사이렌 패턴 반복 (더 급박하게)
      soundIntervalRef.current = setInterval(() => {
        // 인터벌 내에서도 AudioContext 상태 확인
        if (audioContextRef.current && audioContextRef.current.state !== 'closed') {
          playSirenPattern();
        } else {
          if (soundIntervalRef.current) {
            clearInterval(soundIntervalRef.current);
            soundIntervalRef.current = null;
          }
        }
      }, 800);

    } catch (error) {
      // 오류 발생 시 정리
      if (soundIntervalRef.current) {
        clearInterval(soundIntervalRef.current);
        soundIntervalRef.current = null;
      }
      if (gainNodeRef.current) {
        gainNodeRef.current = null;
      }
      if (audioContextRef.current) {
        audioContextRef.current = null;
      }
    }
  }, []);

  // 🔇 사이렌 소리 중지 함수 (안전한 버전)
  const stopEmergencySound = useCallback(() => {
    try {
      // 1. 인터벌 정리 (가장 먼저)
      if (soundIntervalRef.current) {
        clearInterval(soundIntervalRef.current);
        soundIntervalRef.current = null;
      }

      // 2. 오실레이터 정리
      if (oscillatorRef.current) {
        try {
          if (oscillatorRef.current.context && oscillatorRef.current.context.state !== 'closed') {
            oscillatorRef.current.stop();
          }
        } catch (e) {
          // 이미 중지된 경우 무시
        }
        oscillatorRef.current = null;
      }

      // 3. GainNode 먼저 정리 (AudioContext 닫기 전에)
      if (gainNodeRef.current) {
        try {
          gainNodeRef.current.disconnect();
        } catch (e) {
          // 이미 연결 해제된 경우 무시
        }
        gainNodeRef.current = null;
      }

      // 4. AudioContext 정리 (마지막에)
      if (audioContextRef.current) {
        const contextState = audioContextRef.current.state;
        if (contextState !== 'closed') {
          audioContextRef.current.close().then(() => {
          }).catch((e) => {
          });
        }
        audioContextRef.current = null;
      }

    } catch (error) {
      // 강제 정리
      soundIntervalRef.current = null;
      oscillatorRef.current = null;
      gainNodeRef.current = null;
      audioContextRef.current = null;
    }
  }, []);

  // Emergency 트리거 함수 (중복 방지)
  const triggerEmergency = useCallback((alertData) => {
    setEmergencyState(prev => {
      // 🚫 이미 활성화된 상태에서 같은 차트의 같은 조건이면 무시
      if (prev.isActive && prev.lastTrigger) {
        const isSameChart = prev.lastTrigger.chartId === alertData.chartId;
        const isSameCondition = prev.lastTrigger.condition === alertData.condition;
        const isSameValue = prev.lastTrigger.value === alertData.value;
        
        if (isSameChart && isSameCondition && isSameValue) {
          return prev; // 상태 변경 없음
        }
      }
      
      // 🔔 이미 확인된 상태에서는 새로운 Emergency도 확인된 상태로 유지 (오버레이 안 뜸)
      if (prev.isAcknowledged) {
      } else {
        // 🚨 Emergency 사운드 시작 (오버레이가 표시될 때만)
        startEmergencySound();
      }
      
      const newTriggeredBy = [...prev.triggeredBy];
      
      // 이미 트리거된 차트인지 확인
      const existingIndex = newTriggeredBy.findIndex(item => item.chartId === alertData.chartId);
      
      if (existingIndex !== -1) {
        // 기존 차트 정보 업데이트
        newTriggeredBy[existingIndex] = alertData;
      } else {
        // 새로운 차트 추가
        newTriggeredBy.push(alertData);
      }

      return {
        ...prev,
        isActive: true,
        level: 'emergency',
        triggeredBy: newTriggeredBy,
        timestamp: prev.timestamp || new Date().toISOString(),
        lastTrigger: alertData,
        isAcknowledged: prev.isAcknowledged // 기존 확인 상태 유지
      };
    });
  }, []);

  // Emergency 해제 함수 (특정 차트)
  const removeEmergencyTrigger = useCallback((chartId) => {
    setEmergencyState(prev => {
      const newTriggeredBy = prev.triggeredBy.filter(item => item.chartId !== chartId);
      
      return {
        ...prev,
        triggeredBy: newTriggeredBy,
        isActive: newTriggeredBy.length > 0,
        level: newTriggeredBy.length > 0 ? 'emergency' : null,
        timestamp: newTriggeredBy.length > 0 ? prev.timestamp : null
      };
    });
  }, []);

    // Emergency 전체 해제 함수 (완전 초기화)
  const clearEmergency = useCallback(() => {
    // 🔇 사운드 중지
    stopEmergencySound();
    
    setEmergencyState({
      isActive: false,
      level: null,
      triggeredBy: [],
      timestamp: null,
      isAcknowledged: false,
      lastTrigger: null
    });
    
  }, [stopEmergencySound]);

  // Emergency 확인 함수 (오버레이 숨김, 상태는 유지)
  const acknowledgeEmergency = useCallback(() => {
    // 🔇 사운드 중지 (확인하면 소리 멈춤)
    stopEmergencySound();
    
    setEmergencyState(prev => ({
      ...prev,
      isAcknowledged: true // 오버레이 숨김, Emergency 상태는 유지
    }));
  }, [stopEmergencySound]);

  // 사용자 상호작용 감지 및 AudioContext 활성화
  useEffect(() => {
    const handleUserInteraction = () => {
      if (!userInteractedRef.current) {
        userInteractedRef.current = true;
        // 현재 AudioContext가 suspended 상태라면 resume
        if (audioContextRef.current && audioContextRef.current.state === 'suspended') {
          audioContextRef.current.resume().then(() => {
          }).catch(error => {
          });
        }
      }
    };

    // 다양한 사용자 상호작용 이벤트 감지
    document.addEventListener('click', handleUserInteraction, { passive: true });
    document.addEventListener('touchstart', handleUserInteraction, { passive: true });
    document.addEventListener('keydown', handleUserInteraction, { passive: true });

    return () => {
      document.removeEventListener('click', handleUserInteraction);
      document.removeEventListener('touchstart', handleUserInteraction);
      document.removeEventListener('keydown', handleUserInteraction);
    };
  }, []);

  // 🧹 컴포넌트 언마운트 시 사운드 정리
  useEffect(() => {
    return () => {
      stopEmergencySound();
    };
  }, [stopEmergencySound]);

  const value = useMemo(() => ({
    emergencyState,
    triggerEmergency,
    removeEmergencyTrigger,
    clearEmergency,
    acknowledgeEmergency,
    startEmergencySound,
    stopEmergencySound
  }), [
    emergencyState.isActive, 
    emergencyState.level, 
    emergencyState.triggeredBy.length,
    emergencyState.isAcknowledged,
    triggerEmergency, 
    removeEmergencyTrigger, 
    clearEmergency, 
    acknowledgeEmergency, 
    startEmergencySound,
    stopEmergencySound
  ]);

  return (
    <EmergencyContext.Provider value={value}>
      {children}
    </EmergencyContext.Provider>
  );
};

export default EmergencyContext; 